import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Combobox,
  EntryCell,
  EntryGrid,
  EntryRow,
  GridAction,
  GridInput,
  GridSelect,
  Input,
  Select,
  StatTile,
  Textarea,
  cmpDecimal,
  formatDate,
  formatMoney,
  formatQty,
  formatRate,
  humanizeStatus,
  sumDecimal,
} from '@biovet/ui';
import { auth } from '../lib/actor.js';
import { apiSend, useResource } from '../lib/api.js';
import {
  allCurrencies,
  baseCurrency,
  companyToday,
  baseUomOf,
  defaultUom,
  monthOf,
  uomOptions,
} from '../lib/reference-data.js';
import type { RecordSet } from '../lib/design-data.js';
import { RecordGrid } from './Records.js';
import { Fact, FactGrid, RegisterWindow, Th } from './parts.js';
import { printDocument, type PrintDocument } from '../lib/print.js';
import {
  ComboField,
  DocumentPanel,
  EntryPanel,
  FieldRow,
  Messages,
  PanelToggle,
  SelectField,
  SubmitHint,
  TextField,
  Tiles,
  pickedLine,
  today,
  useAction,
  useAutoRows,
  useDefaultUnits,
  useSubmitShortcut,
} from './document.js';


const R = 'right' as const;

function suppliersOfOrigin(
  all: PurchaseRefs['suppliers'],
  origin: 'FOREIGN' | 'LOCAL',
  keep: string,
): PurchaseRefs['suppliers'] {
  return all.filter((s) => s.origin === origin || (keep !== '' && s.code === keep));
}

interface PurchaseRefs {
  suppliers: Array<{
    code: string;
    name: string;
    origin: string;
    currency: string;
    paymentTermsDays: number;
    countryOfOrigin: string | null;
    isFiler: boolean;
    ntn: string | null;
    defaultIncoterm: string | null;
    defaultPortOfLoading: string | null;
    defaultPortOfDischarge: string | null;
    typicalTransitDays: number | null;
    payableAccount: string | null;
  }>;
  ports: Array<{ code: string; name: string; countryCode: string; mode: string }>;
  shippingLines: Array<{ code: string; name: string }>;
  warehouses: Array<{ code: string; name: string; kind: string; isDefault: boolean }>;
  taxCodes: Array<{
    code: string;
    name: string;
    kind: string;
    rate: string;
    isRecoverable: boolean;
  }>;
  reasonCodes: Array<{ code: string; name: string; domain: string; isResaleable: boolean }>;
  uoms: Array<{ code: string; name: string; dimension: string; decimals: number }>;
  chargePayables: Array<{ code: string; name: string; systemKey: string }>;
}

interface ProductOption {
  sku: string;
  name: string;
  brand: string | null;
  categoryName: string | null;
  isActive: boolean;
  isPurchasable: boolean;
  baseUom: string;
  packSizeBase: string | null;
  purchaseTaxPct: string | null;
  trackExpiry: boolean;
  shelfLifeDays: number | null;
}

function useProducts() {
  return useResource<{ products: ProductOption[] }>('/products');
}


interface ShipmentRow {
  shipmentNo: string;
  supplier: string;
  supplierCode: string;
  status: string;
  currency: string;
  invoiceFxRate: string;
  customsFxRate: string | null;
  supplierInvoiceNo: string | null;
  blAwbNo: string | null;
  blDate: string | null;
  billOfEntryNo: string | null;
  billOfEntryDate: string | null;
  portOfLoading: string | null;
  portOfDischarge: string | null;
  etd: string | null;
  eta: string | null;
  actualArrival: string | null;
  containerCount: number | null;
  destinationWarehouse: string | null;
  goodsValueBase: string;
  landedCostBase: string;
  recoverableTaxBase: string;
  totalLandedBase: string;
  transitVoucherNo: string | null;
  landingVoucherNo: string | null;
  grnNo: string | null;
  lineCount: number;
  qtyBase: string;
  goodsValueTxn: string;
  unpostedCharges: number;
}

const SHIPMENT_FILTERS: Array<{ label: string; test: (s: ShipmentRow) => boolean }> = [
  { label: 'Open shipments', test: (s) => !['LANDED', 'CANCELLED'].includes(s.status) },
  { label: 'In transit', test: (s) => s.status === 'SHIPPED' },
  {
    label: 'Under clearance',
    test: (s) => ['ARRIVED', 'UNDER_CLEARANCE', 'CLEARED'].includes(s.status),
  },
  { label: 'Landed', test: (s) => s.status === 'LANDED' },
  { label: 'All', test: () => true },
];

interface ShipmentLineDraft {
  sku: string;
  enteredQty: string;
  uomCode: string;
  unitRateTxn: string;
  grossWeightKg: string;
}

function blankShipmentLine(): ShipmentLineDraft {
  return { sku: '', enteredQty: '', uomCode: defaultUom(), unitRateTxn: '', grossWeightKg: '' };
}

export function ImportShipmentsScreen({
  onOpenCosting,
  sessionFxRate,
}: {
  onOpenCosting?: (no: string) => void;
  sessionFxRate?: string;
}) {
  const [range, setRange] = useState(() => monthOf(companyToday()));
  const res = useResource<{ shipments: ShipmentRow[] }>('/shipments', {
    from: range.from || undefined,
    to: range.to || undefined,
  });
  const refs = useResource<PurchaseRefs>('/purchase-refs');
  const products = useProducts();
  const all = useMemo(() => res.data?.shipments ?? [], [res.data]);
  const [chip, setChip] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [open, setOpen] = useState(false);

  const [header, setHeader] = useState({
    supplierCode: '',
    currency: 'USD',
    invoiceFxRate: sessionFxRate ?? '',
    supplierInvoiceNo: '',
    supplierInvoiceDate: '',
    incoterm: 'CFR',
    blAwbNo: '',
    blDate: today(),
    vesselOrFlight: '',
    containerCount: '',
    countryOfOrigin: '',
    portOfLoadingCode: '',
    portOfDischargeCode: '',
    etd: '',
    eta: '',
    billOfEntryNo: '',
    billOfEntryDate: '',
    customsFxRate: '',
    destinationWarehouse: '',
    narration: '',
  });
  const [lines, setLines] = useState<ShipmentLineDraft[]>([blankShipmentLine()]);

  useEffect(() => {
    const list = refs.data;
    if (!list) return;
    const first = list.suppliers.find((s) => s.origin === 'FOREIGN');
    setHeader((h) => ({
      ...h,
      supplierCode: h.supplierCode || (first?.code ?? ''),
      destinationWarehouse:
        h.destinationWarehouse || (list.warehouses.find((w) => w.isDefault)?.code ?? ''),
      portOfLoadingCode: h.portOfLoadingCode || (first?.defaultPortOfLoading ?? ''),
      portOfDischargeCode: h.portOfDischargeCode || (first?.defaultPortOfDischarge ?? ''),
      incoterm: h.incoterm || (first?.defaultIncoterm ?? 'CFR'),
      countryOfOrigin: h.countryOfOrigin || (first?.countryOfOrigin ?? ''),
      eta: h.eta || addDays(h.blDate, first?.typicalTransitDays),
    }));
  }, [refs.data]);

  const chooseSupplier = (code: string) => {
    const s = (refs.data?.suppliers ?? []).find((x) => x.code === code);
    setHeader((h) => ({
      ...h,
      supplierCode: code,
      currency: s?.currency ?? h.currency,
      portOfLoadingCode: h.portOfLoadingCode || (s?.defaultPortOfLoading ?? ''),
      portOfDischargeCode: h.portOfDischargeCode || (s?.defaultPortOfDischarge ?? ''),
      incoterm: h.incoterm || (s?.defaultIncoterm ?? h.incoterm),
      countryOfOrigin: h.countryOfOrigin || (s?.countryOfOrigin ?? ''),
      eta: h.eta || addDays(h.blDate, s?.typicalTransitDays),
    }));
  };

  const rows = useMemo(() => all.filter(SHIPMENT_FILTERS[chip]?.test ?? (() => true)), [all, chip]);
  const active = selected === null ? null : (rows[selected] ?? null);

  const detail = useResource<ShipmentDetail>(active ? '/shipment' : null, {
    no: active?.shipmentNo ?? undefined,
  });

  const shipmentDoc = useMemo<PrintDocument | null>(() => {
    const d = detail.data;
    if (!d || !active) return null;
    const h = d.header;
    const money = (v: string | null | undefined) => formatMoney(v ?? '0', { decimals: 0 });
    return {
      title: 'Import Consignment',
      documentNo: h.shipmentNo ?? active.shipmentNo,
      facts: [
        {
          label: 'Supplier',
          value: `${h.supplier}${h.supplierCode ? ` (${h.supplierCode})` : ''}`,
        },
        { label: "Supplier's invoice", value: h.supplierInvoiceNo ?? '' },
        { label: 'B/L or AWB', value: h.blAwbNo ?? '' },
        { label: 'B/L date', value: h.blDate ? formatDate(h.blDate) : '' },
        { label: 'Vessel / flight', value: h.vesselOrFlight ?? '' },
        { label: 'Containers', value: h.containerCount ? String(h.containerCount) : '' },
        { label: 'Loading', value: h.portOfLoading ?? '' },
        { label: 'Discharge', value: h.portOfDischarge ?? '' },
        { label: 'ETD', value: h.etd ? formatDate(h.etd) : '' },
        { label: 'ETA', value: h.eta ? formatDate(h.eta) : '' },
        { label: 'Arrived', value: h.actualArrival ? formatDate(h.actualArrival) : '' },
        { label: 'Bill of Entry', value: h.billOfEntryNo ?? '' },
        { label: 'Destination', value: h.destinationWarehouse ?? '' },
        { label: 'Incoterm', value: h.incoterm ?? '' },
        { label: 'Invoice rate', value: `${h.currency} @ ${formatRate(h.invoiceFxRate)}` },
        {
          label: 'Customs rate',
          value: h.customsFxRate ? formatRate(h.customsFxRate) : '',
        },
      ],
      columns: [
        { label: 'Item' },
        { label: 'HS code', width: '110px' },
        { label: 'Qty', align: 'right', width: '120px' },
        { label: `Rate ${h.currency}`, align: 'right', width: '110px' },
        { label: `Goods ${h.currency}`, align: 'right', width: '130px' },
        { label: 'Landed / Kg', align: 'right', width: '120px' },
      ],
      rows: d.lines.map((l) => [
        `${l.sku ?? ''} · ${l.productName ?? ''}`,
        l.hsCode ?? '—',
        `${formatQty(l.enteredQty ?? l.qtyBase, 0)} ${l.uomCode ?? ''}`,
        formatRate(l.unitRateTxn ?? '0', 2),
        formatMoney(l.goodsValueTxn ?? '0', { decimals: 0 }),
        formatRate(l.landedUnitCost ?? '0', 2),
      ]),
      totals: [
        { label: 'Goods value', value: money(h.goodsValueBase) },
        { label: 'Duty, clearing and freight', value: money(h.landedCostBase) },
        { label: 'Total landed cost', value: money(h.totalLandedBase), strong: true },
        { label: 'Recoverable import tax', value: money(h.recoverableTaxBase) },
      ],
      narration: h.narration ?? null,
      notes: [
        h.transitVoucherNo
          ? `Payable booked on ${h.transitVoucherNo}.`
          : 'Payable not booked — it follows the B/L.',
        h.landingVoucherNo ? `Charges landed on ${h.landingVoucherNo}.` : '',
        h.grnNo ? `Received into stock on ${h.grnNo}.` : 'Not yet received into stock.',
      ].filter(Boolean),
      signatures: ['Prepared by', 'Approved by'],
    };
  }, [detail.data, active]);

  const action = useAction(() => {
    res.reload();
    detail.reload();
  });

  useAutoRows(lines, setLines, blankShipmentLine, (l) => l.sku === '' && l.enteredQty === '');
  useDefaultUnits(lines, setLines);
  const postable =
    !action.busy &&
    header.supplierCode !== '' &&
    header.blAwbNo.trim() !== '' &&
    lines.some((l) => l.sku !== '');

  const [editing, setEditing] = useState<string | null>(null);

  const startEdit = () => {
    const d = detail.data;
    if (!d) return;
    const h = d.header;
    setEditing(h.shipmentNo ?? null);
    setHeader({
      supplierCode: h.supplierCode ?? '',
      currency: h.currency ?? 'USD',
      invoiceFxRate: h.invoiceFxRate ?? '',
      supplierInvoiceNo: h.supplierInvoiceNo ?? '',
      supplierInvoiceDate: (h.supplierInvoiceDate ?? '').slice(0, 10),
      incoterm: h.incoterm ?? 'CFR',
      blAwbNo: h.blAwbNo ?? '',
      blDate: (h.blDate ?? '').slice(0, 10),
      vesselOrFlight: h.vesselOrFlight ?? '',
      containerCount: h.containerCount ?? '',
      countryOfOrigin: h.countryOfOrigin ?? '',
      portOfLoadingCode: h.portOfLoadingCode ?? '',
      portOfDischargeCode: h.portOfDischargeCode ?? '',
      etd: (h.etd ?? '').slice(0, 10),
      eta: (h.eta ?? '').slice(0, 10),
      billOfEntryNo: h.billOfEntryNo ?? '',
      billOfEntryDate: (h.billOfEntryDate ?? '').slice(0, 10),
      customsFxRate: h.customsFxRate ?? '',
      destinationWarehouse: h.destinationWarehouse ?? '',
      narration: h.narration ?? '',
    });
    setLines(
      d.lines.map((l) => ({
        sku: l.sku,
        enteredQty: l.enteredQty,
        uomCode: l.uomCode,
        unitRateTxn: l.unitRateTxn,
        grossWeightKg: l.grossWeightKg ?? '',
        hsCode: l.hsCode ?? '',
      })),
    );
    setOpen(true);
  };

  const closePanel = () => {
    setOpen(false);
    setEditing(null);
    setLines([blankShipmentLine()]);
  };

  const save = () => {
    void action.run(() =>
      apiSend<{ shipmentNo: string; message: string }>(
        'POST',
        '/shipments',
        {
          ...header,
          ...(editing ? { shipmentNo: editing } : {}),
          containerCount: header.containerCount || undefined,
          lines: lines
            .filter((l) => l.sku !== '' && l.enteredQty !== '' && l.unitRateTxn !== '')
            .map((l) => ({ ...l, grossWeightKg: l.grossWeightKg || undefined })),
        },
        auth(),
      ).then((r) => {
        closePanel();
        return r;
      }),
    );
  };

  const remove = (shipmentNo: string) => {
    if (
      !window.confirm(
        `Delete ${shipmentNo}?

Nothing has posted against it, so this removes the record. Its number stays consumed — the series is gapless by design.`,
      )
    ) {
      return;
    }
    void action.run(() =>
      apiSend<{ shipmentNo: string; message: string }>(
        'DELETE',
        `/shipments/${encodeURIComponent(shipmentNo)}`,
        undefined,
        auth(),
      ).then((r) => {
        setSelected(null);
        return r;
      }),
    );
  };

  useSubmitShortcut(open && postable, save);

  const advance = (shipmentNo: string, status: string) => {
    void action.run(() =>
      apiSend<{ message: string }>('POST', `/shipments/${shipmentNo}/status`, { status }, auth()),
    );
  };

  const postCharges = (shipmentNo: string) => {
    void action.run(() =>
      apiSend<{ message: string }>('POST', `/shipments/${shipmentNo}/charges`, {}, auth()),
    );
  };

  const set: RecordSet = {
    crumb: 'Sales & Purchase / Import Shipments',
    title: 'Import shipments',
    subtitle: 'Imported consignments, from order through to arrival in the warehouse.',
    chips: SHIPMENT_FILTERS.map((f) => f.label),
    cols: [
      { label: 'Lot no.', width: '150px', mono: true },
      { label: 'Supplier' },
      { label: 'B/L', width: '140px', mono: true },
      { label: 'Qty (Kg)', align: R, mono: true, width: '120px' },
      { label: 'Goods (txn)', align: R, mono: true, width: '130px' },
      { label: 'Landed PKR', align: R, mono: true, width: '150px' },
      { label: 'BoE no.', width: '130px', mono: true },
      { label: 'Stage', align: R, width: '150px', badge: true },
    ],
    rows: rows.map((s) => [
      s.shipmentNo,
      s.supplier,
      s.blAwbNo ?? '—',
      formatQty(s.qtyBase, 0),
      `${s.currency} ${formatMoney(s.goodsValueTxn, { decimals: 0 })}`,
      formatMoney(s.totalLandedBase, { decimals: 0, dashOnZero: true }),
      s.billOfEntryNo ?? '—',
      humanizeStatus(s.status),
    ]),
    total: [
      '',
      `${rows.length} consignment${rows.length === 1 ? '' : 's'}`,
      '',
      formatQty(sumDecimal(rows.map((s) => s.qtyBase)), 0),
      '',
      formatMoney(sumDecimal(rows.map((s) => s.totalLandedBase)), { decimals: 0 }),
      '',
      '',
    ],
  };

  const NEXT: Record<string, string[]> = {
    PLANNED: ['LC_OPENED', 'SHIPPED', 'CANCELLED'],
    LC_OPENED: ['SHIPPED', 'CANCELLED'],
    SHIPPED: ['ARRIVED'],
    ARRIVED: ['UNDER_CLEARANCE'],
    UNDER_CLEARANCE: ['CLEARED'],
    CLEARED: [],
    LANDED: [],
    CANCELLED: [],
  };

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chip}
      onChipChange={(i) => {
        setChip(i);
        setSelected(null);
      }}
      selectedRow={selected}
      onRowSelect={(i) => setSelected(selected === i ? null : i)}
      emptyTitle="No consignment in this stage"
      hideGrid={open}
      actions={
        <PanelToggle
          open={open}
          onToggle={() => (open ? closePanel() : setOpen(true))}
          label="New shipment"
        />
      }
    >
      <Messages notice={action.notice} problem={action.problem} />

      {}
      {!open && (
        <RegisterWindow range={range} onChange={setRange} count={rows.length} noun="consignment" />
      )}

      {active && !open && (
        <Card flush className="mb-3.5" data-noprint="1">
          <CardHeader
            title={`${active.shipmentNo} · ${active.supplier}`}
            subtitle={
              active.transitVoucherNo
                ? `Payable booked on ${active.transitVoucherNo} at ${formatRate(active.invoiceFxRate)}${active.grnNo ? ` · landed on ${active.grnNo}` : ''}`
                : 'Nothing posted yet — the payable follows the B/L'
            }
            actions={
              <>
                {(NEXT[active.status] ?? []).map((next) => (
                  <Button
                    key={next}
                    size="sm"
                    disabled={action.busy}
                    onClick={() => advance(active.shipmentNo, next)}
                  >
                    {humanizeStatus(next)}
                  </Button>
                ))}
                {active.unpostedCharges > 0 && (
                  <Button
                    size="sm"
                    disabled={action.busy}
                    onClick={() => postCharges(active.shipmentNo)}
                  >
                    Post {active.unpostedCharges} charge{active.unpostedCharges === 1 ? '' : 's'}
                  </Button>
                )}
                {shipmentDoc && (
                  <Button size="sm" onClick={() => printDocument(shipmentDoc)}>
                    Print
                  </Button>
                )}
                {}
                {!active.transitVoucherNo && !active.landingVoucherNo && !active.grnNo && (
                  <>
                    <Button size="sm" disabled={action.busy || !detail.data} onClick={startEdit}>
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={action.busy}
                      onClick={() => remove(active.shipmentNo)}
                    >
                      Delete
                    </Button>
                  </>
                )}
                {onOpenCosting && (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => onOpenCosting(active.shipmentNo)}
                  >
                    Costing sheet
                  </Button>
                )}
              </>
            }
          />
          <CardBody>
            <FactGrid>
              <Fact label="Invoice rate" value={formatRate(active.invoiceFxRate)} />
              <Fact
                label="Customs rate"
                value={active.customsFxRate ? formatRate(active.customsFxRate) : '—'}
              />
              <Fact label="Port of loading" value={active.portOfLoading ?? '—'} />
              <Fact label="Port of discharge" value={active.portOfDischarge ?? '—'} />
              <Fact label="ETA" value={active.eta ? formatDate(active.eta) : '—'} />
              <Fact
                label="Recoverable tax"
                value={formatMoney(active.recoverableTaxBase, { decimals: 0, dashOnZero: true })}
              />
            </FactGrid>
          </CardBody>
        </Card>
      )}

      {}
      {active && !open && (
        <DocumentPanel
          doc={shipmentDoc}
          loading={detail.loading}
          error={detail.error}
          onClose={() => setSelected(null)}
        />
      )}

      <EntryPanel
        title={editing ? `Amend ${editing}` : 'New import consignment'}
        subtitle="Goods lines close when the B/L posts — after that the payable depends on them"
        open={open}
        onToggle={closePanel}
        openLabel="New shipment"
        actions={
          <>
            <SubmitHint />
            <Button size="sm" variant="primary" disabled={!postable} onClick={save}>
              {editing ? 'Save changes' : 'Save consignment'}
            </Button>
          </>
        }
      >
        <FieldRow>
          <ComboField
            label="Supplier"
            required
            value={header.supplierCode}
            options={suppliersOfOrigin(
              refs.data?.suppliers ?? [],
              'FOREIGN',
              header.supplierCode,
            ).map((s) => ({
              value: s.code,
              label: `${s.name}${s.origin === 'FOREIGN' ? '' : ' (local)'}`,
              hint: `${s.payableAccount ?? 'no account mapped'} · ${s.currency}`,
              keywords: `${s.code} ${s.ntn ?? ''} ${s.payableAccount ?? ''}`,
            }))}
            onChange={(v) => chooseSupplier(v)}
          />
          <SelectField
            label="Currency"
            required
            value={header.currency}
            placeholder=""
            options={allCurrencies().map((c) => ({ value: c.code, label: c.code }))}
            onChange={(v) =>
              setHeader({
                ...header,
                currency: v,
                invoiceFxRate: v === 'PKR' ? '1' : header.invoiceFxRate,
              })
            }
          />
          <TextField
            label="Invoice-date rate"
            required
            hint="Books Accounts Payable — Foreign"
            value={header.invoiceFxRate}
            onChange={(v) => setHeader({ ...header, invoiceFxRate: v })}
          />
          <ComboField
            label="Destination warehouse"
            value={header.destinationWarehouse}
            options={(refs.data?.warehouses ?? []).map((w) => ({
              value: w.code,
              label: w.name,
              hint: w.code,
            }))}
            onChange={(v) => setHeader({ ...header, destinationWarehouse: v })}
          />
        </FieldRow>

        <FieldRow>
          <TextField
            label="Supplier invoice no."
            value={header.supplierInvoiceNo}
            onChange={(v) => setHeader({ ...header, supplierInvoiceNo: v })}
          />
          <TextField
            label="B/L or AWB no."
            required
            hint="The consignment's identity at the port — nothing is released against a number nobody has"
            value={header.blAwbNo}
            onChange={(v) => setHeader({ ...header, blAwbNo: v })}
          />
          <TextField
            label="B/L date"
            type="date"
            hint="The date the payable is booked on"
            value={header.blDate}
            onChange={(v) => setHeader({ ...header, blDate: v })}
          />
          <TextField
            label="Vessel or flight"
            value={header.vesselOrFlight}
            onChange={(v) => setHeader({ ...header, vesselOrFlight: v })}
          />
        </FieldRow>

        <FieldRow>
          <ComboField
            label="Port of loading"
            value={header.portOfLoadingCode}
            options={(refs.data?.ports ?? []).map((p) => ({
              value: p.code,
              label: `${p.code} · ${p.name}`,
              hint: p.countryCode,
            }))}
            onChange={(v) => setHeader({ ...header, portOfLoadingCode: v })}
          />
          <ComboField
            label="Port of discharge"
            value={header.portOfDischargeCode}
            options={(refs.data?.ports ?? []).map((p) => ({
              value: p.code,
              label: `${p.code} · ${p.name}`,
              hint: p.countryCode,
            }))}
            onChange={(v) => setHeader({ ...header, portOfDischargeCode: v })}
          />
          <TextField
            label="ETD"
            type="date"
            value={header.etd}
            onChange={(v) => setHeader({ ...header, etd: v })}
          />
          <TextField
            label="ETA"
            type="date"
            value={header.eta}
            onChange={(v) => setHeader({ ...header, eta: v })}
          />
        </FieldRow>

        <EntryGrid
          aria-label="Consignment lines"
          columns={[
            { key: 'no', label: '#', width: '44px', noPrint: true },
            { key: 'sku', label: 'SKU', width: '240px', required: true },
            { key: 'qty', label: 'Qty', width: '120px', numeric: true, required: true },
            { key: 'uom', label: 'Unit', width: '100px' },
            { key: 'rate', label: 'FOB / CFR rate', width: '140px', numeric: true, required: true },
            { key: 'wt', label: 'Gross Kg', width: '130px', numeric: true },
            { key: 'del', label: '', width: '44px', noPrint: true },
          ]}
        >
          {lines.map((line, i) => (
            <EntryRow key={i}>
              <EntryCell padded className="text-[var(--ink-3)]">
                {i + 1}
              </EntryCell>
              <EntryCell>
                {}
                <Combobox
                  value={line.sku}
                  aria-label={`Line ${i + 1} item`}
                  placeholder="Type a SKU or item name…"
                  emptyText="No purchasable item matches"
                  options={(products.data?.products ?? [])
                    .filter((p) => p.isActive && p.isPurchasable)
                    .map((p) => ({
                      value: p.sku,
                      label: `${p.sku} · ${p.name}`,
                      keywords: `${p.brand ?? ''} ${p.categoryName ?? ''}`,
                    }))}
                  onChange={(sku) => {
                    const picked = pickedLine(
                      sku,
                      products.data?.products,
                      'PURCHASE',
                      line.enteredQty,
                    );
                    const next = [...lines];
                    next[i] = { ...line, sku, enteredQty: picked.qty, uomCode: picked.uom };
                    setLines(next);
                  }}
                />
              </EntryCell>
              <EntryCell>
                <GridInput
                  numeric
                  value={line.enteredQty}
                  aria-label={`Line ${i + 1} quantity`}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...line, enteredQty: e.target.value };
                    setLines(next);
                  }}
                />
              </EntryCell>
              <EntryCell>
                <GridSelect
                  value={line.uomCode}
                  aria-label={`Line ${i + 1} unit`}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...line, uomCode: e.target.value };
                    setLines(next);
                  }}
                >
                  {}
                  {uomOptions(line.uomCode, baseUomOf(line.sku, products.data?.products)).map(
                    (u) => (
                      <option key={u.code} value={u.code}>
                        {u.code}
                      </option>
                    ),
                  )}
                </GridSelect>
              </EntryCell>
              <EntryCell>
                <GridInput
                  numeric
                  value={line.unitRateTxn}
                  aria-label={`Line ${i + 1} rate`}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...line, unitRateTxn: e.target.value };
                    setLines(next);
                  }}
                />
              </EntryCell>
              <EntryCell>
                <GridInput
                  numeric
                  value={line.grossWeightKg}
                  placeholder="freight basis"
                  aria-label={`Line ${i + 1} gross weight`}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...line, grossWeightKg: e.target.value };
                    setLines(next);
                  }}
                />
              </EntryCell>
              <EntryCell noPrint>
                <GridAction
                  label={`Remove line ${i + 1}`}
                  disabled={lines.length === 1}
                  onClick={() => setLines(lines.filter((_, j) => j !== i))}
                >
                  ×
                </GridAction>
              </EntryCell>
            </EntryRow>
          ))}
        </EntryGrid>

        {}
        <div className="flex flex-col">
          <label
            htmlFor="shipment-narration"
            className="bb-eyebrow pb-1.5 text-[10px] text-[var(--ink-3)]"
          >
            Narration
          </label>
          <Textarea
            id="shipment-narration"
            rows={2}
            value={header.narration}
            placeholder="Indent reference, LC number, shipping terms"
            aria-label="Narration"
            onChange={(e) => setHeader({ ...header, narration: e.target.value })}
          />
        </div>
      </EntryPanel>
    </RecordGrid>
  );
}


interface CostingLine {
  lineNo: number;
  sku: string;
  productName: string;
  qtyBase: string;
  enteredQty: string;
  uomCode: string;
  grossWeightKg: string | null;
  unitRateTxn: string;
  goodsValueTxn: string;
  goodsValueBase: string;
  hsCode: string | null;
  apportionedCostBase: string;
  landedValueBase: string;
  landedUnitCost: string;
  landedCostPerPack: string | null;
  upliftPct: string | null;
  receivedQtyBase: string;
  shortageQtyBase: string;
}

interface CostComponent {
  lineNo: number;
  kind: string;
  description: string;
  treatment: string;
  basis: string;
  currency: string;
  fxRate: string;
  amountTxn: string;
  amountBase: string;
  invoiceNo: string | null;
  invoiceDate: string | null;
  isEstimated: boolean;
  vendorCode: string | null;
  vendor: string | null;
  payableAccount: string | null;
  payableAccountName: string | null;
  voucherNo: string | null;
}

interface ShipmentDetail {
  header: Record<string, string | null>;
  lines: CostingLine[];
  components: CostComponent[];
}

const COMPONENT_KINDS = [
  'FREIGHT',
  'INSURANCE',
  'CUSTOMS_DUTY',
  'REGULATORY_DUTY',
  'SALES_TAX',
  'PORT_TERMINAL',
  'DEMURRAGE',
  'DO_CHARGES',
  'CLEARING_AGENT',
  'INLAND_FREIGHT',
  'LC_CHARGES',
  'OTHER',
];

export function ImportCostingScreen({
  shipmentNo,
  onShipmentChange,
}: {
  shipmentNo: string | null;
  onShipmentChange: (no: string | null) => void;
}) {
  const list = useResource<{ shipments: ShipmentRow[] }>('/shipments');
  const refs = useResource<PurchaseRefs>('/purchase-refs');
  const shipments = list.data?.shipments ?? [];
  const chosen = shipmentNo ?? shipments[0]?.shipmentNo ?? null;
  const res = useResource<ShipmentDetail>(chosen ? '/shipment' : null, { no: chosen ?? undefined });
  const action = useAction(() => {
    res.reload();
    list.reload();
  });

  const [component, setComponent] = useState({
    kind: 'FREIGHT',
    description: '',
    treatment: 'CAPITALIZE',
    basis: 'WEIGHT',
    currency: 'PKR',
    fxRate: '1',
    amountTxn: '',
    payableAccountCode: '',
    invoiceNo: '',
  });

  useEffect(() => {
    const first = refs.data?.chargePayables?.[0]?.code;
    if (!first) return;
    setComponent((c) => (c.payableAccountCode ? c : { ...c, payableAccountCode: first }));
  }, [refs.data]);

  const detail = res.data;
  const header = detail?.header ?? {};
  const lines = detail?.lines ?? [];
  const components = detail?.components ?? [];
  const status = (header.status as string | undefined) ?? '';
  const landed = status === 'LANDED';

  const capitalised = components.filter((c) => c.treatment === 'CAPITALIZE');
  const recoverable = components.filter((c) => c.treatment === 'RECOVERABLE');
  const expensed = components.filter((c) => c.treatment === 'EXPENSE');
  const goodsBase = (header.goodsValueBase as string) ?? '0';
  const totalLanded = (header.totalLandedBase as string) ?? '0';
  const totalQty = sumDecimal(lines.map((l) => l.qtyBase));

  const addComponent = () => {
    if (!chosen) return;
    void action.run(() =>
      apiSend<{ message: string }>(
        'POST',
        `/shipments/${chosen}/components`,
        {
          ...component,
          description: component.description || undefined,
          payableAccountCode: component.payableAccountCode || undefined,
          invoiceNo: component.invoiceNo || undefined,
        },
        auth(),
      ).then((r) => {
        setComponent({ ...component, amountTxn: '', description: '', invoiceNo: '' });
        return r;
      }),
    );
  };

  const removeComponent = (lineNo: number) => {
    if (!chosen) return;
    void action.run(() =>
      apiSend<{ message: string }>(
        'DELETE',
        `/shipments/${chosen}/components/${lineNo}`,
        undefined,
        auth(),
      ),
    );
  };

  const postCharges = () => {
    if (!chosen) return;
    void action.run(() =>
      apiSend<{ message: string }>('POST', `/shipments/${chosen}/charges`, {}, auth()),
    );
  };

  const land = () => {
    if (!chosen) return;
    void action.run(() =>
      apiSend<{ message: string }>('POST', `/shipments/${chosen}/land`, {}, auth()),
    );
  };

  if (!chosen) {
    return (
      <>
        <PageHeaderless />
        <Card>
          <CardBody>
            <Callout tone="info" title="No consignment to cost yet">
              An import costing sheet is built on a shipment. Raise one under{' '}
              <strong>Sales &amp; Purchase → Import Shipments</strong> and it will appear here.
            </Callout>
          </CardBody>
        </Card>
      </>
    );
  }

  const unposted = components.filter((c) => !c.voucherNo).length;

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-3 pb-3.5">
        <div>
          <div className="bb-eyebrow pb-1.5">Sales &amp; Purchase / Import Costing Sheet</div>
          <h1 className="m-0 text-[20px] leading-tight font-semibold">Import costing — {chosen}</h1>
          <p className="mt-1.5 max-w-[70ch] text-[12.5px] text-[var(--ink-3)]">
            Cost buildup apportioned to a per-Kg landed cost, then pushed into inventory valuation
            at the GRN. Import sales tax is claimed as input tax, never capitalised.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-noprint="1">
          <Select
            value={chosen}
            aria-label="Choose a consignment"
            onChange={(e) => onShipmentChange(e.target.value)}
          >
            {shipments.map((s) => (
              <option key={s.shipmentNo} value={s.shipmentNo}>
                {s.shipmentNo} · {s.supplier}
              </option>
            ))}
          </Select>
          {unposted > 0 && (
            <Button size="sm" onClick={postCharges} disabled={action.busy}>
              Post {unposted} charge{unposted === 1 ? '' : 's'}
            </Button>
          )}
          <Button size="sm" onClick={() => window.print()}>
            Print
          </Button>
          <Button size="sm" variant="primary" onClick={land} disabled={action.busy || landed}>
            {landed ? 'Landed' : 'Post GRN at landed cost'}
          </Button>
        </div>
      </div>

      <Messages notice={action.notice} problem={action.problem} />
      {res.error && (
        <Callout tone="danger" title="This consignment could not be loaded">
          {res.error}
        </Callout>
      )}

      <Card>
        <CardBody>
          <FactGrid>
            <Fact label="Supplier" value={(header.supplier as string) ?? '—'} />
            <Fact label="B/L" value={(header.blAwbNo as string) ?? '—'} />
            <Fact label="Bill of Entry" value={(header.billOfEntryNo as string) ?? '—'} />
            <Fact label="Port of discharge" value={(header.portOfDischarge as string) ?? '—'} />
            <Fact label="Quantity" value={`${formatQty(totalQty, 0)} Kg`} />
            <Fact label="Stage" value={<Badge status={status}>{humanizeStatus(status)}</Badge>} />
          </FactGrid>
        </CardBody>
      </Card>

      <div className="grid gap-3.5 pt-3.5 xl:grid-cols-[1.65fr_1fr]">
        <Card flush data-rpt="1">
          <CardHeader
            title="Cost buildup"
            subtitle="Capitalised components apportioned by their basis; recoverable tax listed under the total"
          />
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr className="bg-[var(--surface-2)]">
                  <Th>Cost component</Th>
                  <Th>Basis</Th>
                  <Th align="right" width="130px">
                    Txn
                  </Th>
                  <Th align="right" width="150px">
                    PKR
                  </Th>
                  <Th align="right" width="120px">
                    Per Kg
                  </Th>
                  <Th align="right" width="60px">
                    {' '}
                  </Th>
                </tr>
              </thead>
              <tbody>
                <tr className="bb-row-h border-b border-[var(--line-2)]">
                  <td className="px-[var(--pad)] text-[13px]">Goods value (invoice)</td>
                  <td className="px-2.5 text-[12px] text-[var(--ink-3)]">Direct per line</td>
                  <td className="bb-num px-2.5 text-right text-[12.5px] tabular-nums">
                    {header.currency}{' '}
                    {formatMoney(sumDecimal(lines.map((l) => l.goodsValueTxn)), { decimals: 0 })}
                  </td>
                  <td className="bb-num px-2.5 text-right text-[13px] tabular-nums">
                    {formatMoney(goodsBase, { decimals: 0 })}
                  </td>
                  <td className="bb-num px-[var(--pad)] text-right text-[13px] tabular-nums">
                    {cmpDecimal(totalQty, '0') > 0
                      ? formatRate((Number(goodsBase) / Number(totalQty)).toFixed(4), 2)
                      : '—'}
                  </td>
                  <td />
                </tr>

                {capitalised.map((c) => (
                  <tr
                    key={c.lineNo}
                    className="bb-row-h border-b border-[var(--line-2)] hover:bg-[var(--surface-2)]"
                  >
                    <td className="px-[var(--pad)] text-[13px]">
                      {c.description}
                      {c.voucherNo && (
                        <span className="bb-num pl-2 text-[11px] text-[var(--ink-3)]">
                          {c.voucherNo}
                        </span>
                      )}
                    </td>
                    <td className="px-2.5 text-[12px] text-[var(--ink-3)]">
                      By {c.basis.toLowerCase()}
                    </td>
                    <td className="bb-num px-2.5 text-right text-[12.5px] tabular-nums">
                      {c.currency} {formatMoney(c.amountTxn, { decimals: 0 })}
                    </td>
                    <td className="bb-num px-2.5 text-right text-[13px] tabular-nums">
                      {formatMoney(c.amountBase, { decimals: 0 })}
                    </td>
                    <td className="bb-num px-[var(--pad)] text-right text-[13px] tabular-nums">
                      {cmpDecimal(totalQty, '0') > 0
                        ? formatRate((Number(c.amountBase) / Number(totalQty)).toFixed(4), 2)
                        : '—'}
                    </td>
                    <td className="px-2 text-right" data-noprint="1">
                      {!c.voucherNo && !landed && (
                        <button
                          type="button"
                          aria-label={`Remove ${c.description}`}
                          className="cursor-pointer border-0 bg-transparent text-[var(--ink-3)] hover:text-[var(--debit)]"
                          onClick={() => removeComponent(c.lineNo)}
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

                <tr className="bb-row-h border-b border-[var(--line-2)] bg-[var(--surface-2)] font-semibold">
                  <td className="px-[var(--pad)] text-[13px]">= Landed cost</td>
                  <td className="px-2.5 text-[12px] text-[var(--ink-3)]">Pushed to inventory</td>
                  <td />
                  <td className="bb-num px-2.5 text-right text-[13px] tabular-nums">
                    {formatMoney(totalLanded, { decimals: 0 })}
                  </td>
                  <td className="bb-num px-[var(--pad)] text-right text-[13px] tabular-nums">
                    {cmpDecimal(totalQty, '0') > 0
                      ? formatRate((Number(totalLanded) / Number(totalQty)).toFixed(4), 2)
                      : '—'}
                  </td>
                  <td />
                </tr>

                {[...recoverable, ...expensed].map((c) => (
                  <tr key={c.lineNo} className="bb-row-h border-b border-[var(--line-2)]">
                    <td className="px-[var(--pad)] text-[12.5px] text-[var(--ink-2)]">
                      {c.description}
                      <span className="pl-2 text-[11px] text-[var(--ink-3)]">
                        {c.treatment === 'RECOVERABLE' ? 'claimed as input tax' : 'to the P&L'}
                      </span>
                    </td>
                    <td className="px-2.5 text-[12px] text-[var(--ink-3)]">Not in stock value</td>
                    <td className="bb-num px-2.5 text-right text-[12.5px] tabular-nums">
                      {c.currency} {formatMoney(c.amountTxn, { decimals: 0 })}
                    </td>
                    <td className="bb-num px-2.5 text-right text-[12.5px] tabular-nums text-[var(--ink-2)]">
                      {formatMoney(c.amountBase, { decimals: 0 })}
                    </td>
                    <td />
                    <td className="px-2 text-right" data-noprint="1">
                      {!c.voucherNo && !landed && (
                        <button
                          type="button"
                          aria-label={`Remove ${c.description}`}
                          className="cursor-pointer border-0 bg-transparent text-[var(--ink-3)] hover:text-[var(--debit)]"
                          onClick={() => removeComponent(c.lineNo)}
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!landed && (
            <CardBody
              className="flex flex-wrap items-end gap-3 border-t border-[var(--line)]"
              data-noprint="1"
            >
              <div className="w-[170px]">
                <div className="bb-eyebrow pb-1.5">Component</div>
                <Select
                  value={component.kind}
                  aria-label="Component kind"
                  onChange={(e) =>
                    setComponent({
                      ...component,
                      kind: e.target.value,
                      treatment: e.target.value === 'SALES_TAX' ? 'RECOVERABLE' : 'CAPITALIZE',
                    })
                  }
                >
                  {COMPONENT_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {humanizeStatus(k)}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="w-[150px]">
                <div className="bb-eyebrow pb-1.5">Treatment</div>
                <Select
                  value={component.treatment}
                  aria-label="Treatment"
                  onChange={(e) => setComponent({ ...component, treatment: e.target.value })}
                >
                  <option value="CAPITALIZE">Capitalise</option>
                  <option value="RECOVERABLE">Recoverable tax</option>
                  <option value="EXPENSE">Expense</option>
                </Select>
              </div>
              <div className="w-[130px]">
                <div className="bb-eyebrow pb-1.5">Basis</div>
                <Select
                  value={component.basis}
                  aria-label="Apportionment basis"
                  onChange={(e) => setComponent({ ...component, basis: e.target.value })}
                >
                  {['VALUE', 'WEIGHT', 'QUANTITY', 'VOLUME', 'CONTAINER'].map((b) => (
                    <option key={b} value={b}>
                      {humanizeStatus(b)}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="w-[150px]">
                <div className="bb-eyebrow pb-1.5">Amount</div>
                <Input
                  value={component.amountTxn}
                  aria-label="Amount"
                  onChange={(e) => setComponent({ ...component, amountTxn: e.target.value })}
                />
              </div>
              {}
              <div className="w-[260px]">
                <div className="bb-eyebrow pb-1.5">Payable to</div>
                <Select
                  value={component.payableAccountCode}
                  aria-label="Payable to"
                  onChange={(e) =>
                    setComponent({ ...component, payableAccountCode: e.target.value })
                  }
                >
                  {(refs.data?.chargePayables ?? []).map((a) => (
                    <option key={a.code} value={a.code}>
                      {a.code} {a.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="min-w-[180px] flex-1">
                <div className="bb-eyebrow pb-1.5">Description</div>
                <Input
                  value={component.description}
                  aria-label="Description"
                  placeholder="Ocean freight, terminal handling…"
                  onChange={(e) => setComponent({ ...component, description: e.target.value })}
                />
              </div>
              <Button
                size="sm"
                variant="primary"
                disabled={action.busy || component.amountTxn === ''}
                onClick={addComponent}
              >
                Add cost
              </Button>
            </CardBody>
          )}
        </Card>

        <div className="flex flex-col gap-3.5">
          <Card flush>
            <CardHeader
              title="FX rate policy applied"
              subtitle="Three distinct rates on one consignment"
            />
            <CardBody className="flex flex-col gap-2.5">
              {[
                ['Supplier payable — invoice date', header.invoiceFxRate],
                ['Customs duty — Bill of Entry date', header.customsFxRate],
                ['Inventory landed cost', header.invoiceFxRate],
              ].map(([label, rate]) => (
                <div key={label as string} className="flex justify-between text-[12.5px]">
                  <span className="text-[var(--ink-2)]">{label}</span>
                  <span className="bb-num font-medium tabular-nums">
                    {rate ? formatRate(rate as string) : '—'}
                  </span>
                </div>
              ))}
              <div className="border-t border-[var(--line-2)] pt-2.5 text-[11.5px] text-[var(--ink-3)]">
                The settlement rate is applied when the supplier is paid; the difference from the
                invoice rate is realised FX gain or loss and does not touch the landed cost.
              </div>
            </CardBody>
          </Card>

          <Card flush>
            <CardHeader
              title="GL effect at GRN"
              subtitle="Transit and port accounts clear to stock"
            />
            <CardBody className="flex flex-col gap-2.5">
              <div className="flex justify-between text-[12.5px]">
                <span className="font-medium text-[var(--debit)]">Stock in Warehouse</span>
                <span className="bb-num tabular-nums">
                  {formatMoney(totalLanded, { decimals: 0 })}
                </span>
              </div>
              <div className="flex justify-between text-[12.5px]">
                <span className="text-[var(--credit)]">Goods in Transit</span>
                <span className="bb-num tabular-nums">
                  {formatMoney(goodsBase, { decimals: 0 })}
                </span>
              </div>
              <div className="flex justify-between text-[12.5px]">
                <span className="text-[var(--credit)]">Goods at Port</span>
                <span className="bb-num tabular-nums">
                  {formatMoney((header.landedCostBase as string) ?? '0', { decimals: 0 })}
                </span>
              </div>
              <div className="flex justify-between border-t border-[var(--line-2)] pt-2.5 text-[12px] text-[var(--ink-3)]">
                <span>Input sales tax — claimed, not capitalised</span>
                <span className="bb-num">
                  {formatMoney((header.recoverableTaxBase as string) ?? '0', {
                    decimals: 0,
                    dashOnZero: true,
                  })}
                </span>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      <Card flush data-rpt="1" className="mt-3.5">
        <CardHeader
          title="Per-line landed cost"
          subtitle="What each SKU costs per Kg and per pack once every capitalised charge is apportioned"
        />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] border-collapse">
            <thead>
              <tr className="bg-[var(--surface-2)]">
                <Th width="150px">SKU</Th>
                <Th>Item</Th>
                <Th align="right" width="120px">
                  Qty (Kg)
                </Th>
                <Th align="right" width="150px">
                  Goods PKR
                </Th>
                <Th align="right" width="150px">
                  Apportioned
                </Th>
                <Th align="right" width="130px">
                  Per Kg
                </Th>
                <Th align="right" width="130px">
                  Per pack
                </Th>
                <Th align="right" width="90px">
                  Uplift
                </Th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.lineNo} className="bb-row-h border-b border-[var(--line-2)]">
                  <td className="bb-num px-[var(--pad)] text-[12px] text-[var(--accent)]">
                    {l.sku}
                  </td>
                  <td className="px-2.5 text-[13px]">{l.productName}</td>
                  <td className="bb-num px-2.5 text-right text-[13px] tabular-nums">
                    {formatQty(l.qtyBase, 0)}
                  </td>
                  <td className="bb-num px-2.5 text-right text-[13px] tabular-nums">
                    {formatMoney(l.goodsValueBase, { decimals: 0 })}
                  </td>
                  <td className="bb-num px-2.5 text-right text-[13px] tabular-nums">
                    {formatMoney(l.apportionedCostBase, { decimals: 0 })}
                  </td>
                  <td className="bb-num px-2.5 text-right text-[13px] tabular-nums">
                    {formatRate(l.landedUnitCost, 2)}
                  </td>
                  <td className="bb-num px-2.5 text-right text-[13px] tabular-nums">
                    {l.landedCostPerPack ? formatMoney(l.landedCostPerPack, { decimals: 0 }) : '—'}
                  </td>
                  <td className="bb-num px-[var(--pad)] text-right text-[12.5px] tabular-nums text-[var(--ink-2)]">
                    {l.upliftPct ? `${l.upliftPct}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function PageHeaderless() {
  return (
    <div className="pb-3.5">
      <div className="bb-eyebrow pb-1.5">Sales &amp; Purchase / Import Costing Sheet</div>
      <h1 className="m-0 text-[20px] leading-tight font-semibold">Import costing</h1>
    </div>
  );
}


interface PurchaseRow {
  invoiceNo: string;
  invoiceDate: string;
  dueDate: string | null;
  supplier: string;
  supplierCode: string;
  supplierInvoiceNo: string | null;
  isImport: boolean;
  currency: string;
  fxRate: string;
  status: string;
  warehouseCode: string | null;
  shipmentNo: string | null;
  grnNo: string | null;
  subtotal: string;
  discountTotal: string;
  taxTotal: string;
  grandTotal: string;
  grandTotalBase: string;
  paidBase: string;
  outstandingBase: string;
  voucherNo: string | null;
  narration: string | null;
  lineCount: number;
  qtyBase: string;
}

interface PurchaseLineDraft {
  sku: string;
  enteredQty: string;
  uomCode: string;
  unitRate: string;
  discountPct: string;
  taxPct: string;
  batchNo: string;
  manufactureDate: string;
  expiryDate: string;
  expiryTouched: boolean;
}

function blankPurchaseLine(): PurchaseLineDraft {
  return {
    sku: '',
    enteredQty: '',
    uomCode: defaultUom(),
    unitRate: '',
    discountPct: '0',
    taxPct: '18',
    batchNo: '',
    manufactureDate: '',
    expiryDate: '',
    expiryTouched: false,
  };
}

function addDays(from: string, days: number | null | undefined): string {
  if (!from || !days) return '';
  const at = new Date(`${from}T00:00:00Z`);
  if (Number.isNaN(at.getTime())) return '';
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

const PURCHASE_FILTERS: Array<{ label: string; test: (p: PurchaseRow) => boolean }> = [
  { label: 'All', test: () => true },
  { label: 'Import', test: (p) => p.isImport },
  { label: 'Local', test: (p) => !p.isImport },
  { label: 'Unpaid', test: (p) => cmpDecimal(p.outstandingBase, '0') > 0 },
];

export function PurchaseEntryScreen() {
  const [range, setRange] = useState(() => monthOf(companyToday()));
  const res = useResource<{ purchases: PurchaseRow[] }>('/purchases', {
    from: range.from || undefined,
    to: range.to || undefined,
  });
  const refs = useResource<PurchaseRefs>('/purchase-refs');
  const products = useProducts();
  const all = useMemo(() => res.data?.purchases ?? [], [res.data]);
  const [chip, setChip] = useState(0);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const action = useAction(res.reload);

  const [expanded, setExpanded] = useState<string | null>(null);
  const detail = useResource<{ header: Record<string, string>; lines: Record<string, string>[] }>(
    expanded ? '/purchase' : null,
    { no: expanded ?? undefined },
  );

  const purchaseDoc = useMemo<PrintDocument | null>(() => {
    const d = detail.data;
    if (!d || !expanded) return null;
    const h = d.header;
    const currency = h.currency ?? baseCurrency();
    const money = (v: string | null | undefined) => formatMoney(v ?? '0', { currency });
    return {
      title: h.isImport ? 'Purchase Invoice — Import' : 'Purchase Invoice',
      documentNo: h.invoiceNo ?? expanded,
      facts: [
        { label: 'Date', value: formatDate(h.invoiceDate ?? '') },
        {
          label: 'Supplier',
          value: `${h.supplier ?? ''}${h.supplierCode ? ` (${h.supplierCode})` : ''}`,
        },
        { label: "Supplier's invoice", value: h.supplierInvoiceNo ?? '' },
        { label: 'Warehouse', value: h.warehouseName ?? h.warehouseCode ?? '' },
        { label: 'Due', value: h.dueDate ? formatDate(h.dueDate) : '' },
        {
          label: 'Currency',
          value: currency === baseCurrency() ? currency : `${currency} @ ${h.fxRate ?? '1'}`,
        },
      ],
      columns: [
        { label: 'Item' },
        { label: 'Batch', width: '150px' },
        { label: 'Qty', align: 'right', width: '110px' },
        { label: 'Rate', align: 'right', width: '110px' },
        { label: 'Tax', align: 'right', width: '90px' },
        { label: 'Amount', align: 'right', width: '130px' },
      ],
      rows: d.lines.map((l) => [
        `${l.sku ?? ''} · ${l.productName ?? ''}`,
        l.batchNo ?? '—',
        `${formatQty(l.enteredQty, 0)} ${l.uomCode ?? ''}`,
        formatRate(l.unitRate, 2),
        `${Number(l.taxPct ?? 0)}%`,
        money(l.lineTotal),
      ]),
      totals: [
        { label: 'Subtotal', value: money(h.subtotal) },
        ...(Number(h.discountTotal ?? 0) !== 0
          ? [{ label: 'Discount', value: `(${money(h.discountTotal)})` }]
          : []),
        ...(Number(h.taxTotal ?? 0) !== 0
          ? [{ label: 'Input tax', value: money(h.taxTotal) }]
          : []),
        { label: 'Grand total', value: money(h.grandTotal), strong: true },
        ...(currency !== baseCurrency()
          ? [{ label: `Booked in ${baseCurrency()}`, value: formatMoney(h.grandTotalBase ?? '0') }]
          : []),
        ...(Number(h.outstandingBase ?? 0) !== 0
          ? [{ label: `Outstanding (${baseCurrency()})`, value: formatMoney(h.outstandingBase) }]
          : []),
      ],
      narration: h.narration ?? null,
      notes: [h.voucherNo ? `Posted as ${h.voucherNo}.` : 'Not posted to the general ledger.'],
      signatures: ['Checked by', 'Approved by'],
    };
  }, [detail.data, expanded]);

  const [header, setHeader] = useState({
    invoiceDate: today(),
    supplierCode: '',
    warehouse: '',
    supplierInvoiceNo: '',
    narration: '',
  });
  const [lines, setLines] = useState<PurchaseLineDraft[]>([blankPurchaseLine()]);

  useEffect(() => {
    const list = refs.data;
    if (!list) return;
    setHeader((h) => ({
      ...h,
      supplierCode:
        h.supplierCode || (list.suppliers.find((s) => s.origin === 'LOCAL')?.code ?? ''),
      warehouse: h.warehouse || (list.warehouses.find((w) => w.isDefault)?.code ?? ''),
    }));
  }, [refs.data]);

  const rows = useMemo(() => {
    const test = PURCHASE_FILTERS[chip]?.test ?? (() => true);
    const needle = search.trim().toLowerCase();
    return all.filter(
      (p) =>
        test(p) &&
        (needle === '' ||
          [p.invoiceNo, p.supplier, p.supplierInvoiceNo ?? ''].some((v) =>
            v.toLowerCase().includes(needle),
          )),
    );
  }, [all, chip, search]);

  useAutoRows(lines, setLines, blankPurchaseLine, (l) => l.sku === '' && l.enteredQty === '');
  useDefaultUnits(lines, setLines);
  const postable =
    !action.busy &&
    header.supplierCode !== '' &&
    header.warehouse !== '' &&
    lines.some((l) => l.sku !== '' && l.enteredQty !== '' && l.unitRate !== '');

  const save = () => {
    void action.run(() =>
      apiSend<{ invoiceNo: string; message: string }>(
        'POST',
        '/purchases',
        {
          ...header,
          lines: lines
            .filter((l) => l.sku !== '' && l.enteredQty !== '' && l.unitRate !== '')
            .map((l) => ({
              sku: l.sku,
              enteredQty: l.enteredQty,
              uomCode: l.uomCode,
              unitRate: l.unitRate,
              discountPct: l.discountPct,
              taxPct: l.taxPct,
              batchNo: l.batchNo || undefined,
              manufactureDate: l.manufactureDate || undefined,
              expiryDate: l.expiryDate || undefined,
            })),
        },
        auth(),
      ).then((r) => {
        setOpen(false);
        setLines([blankPurchaseLine()]);
        return r;
      }),
    );
  };

  useSubmitShortcut(open && postable, save);

  const set: RecordSet = {
    crumb: 'Sales & Purchase / Purchase Entry',
    title: 'Purchase register',
    subtitle: 'Every purchase invoice entered, local and imported.',
    chips: PURCHASE_FILTERS.map((f) => f.label),
    cols: [
      { label: 'Voucher', width: '150px', mono: true },
      { label: 'Date', width: '104px', mono: true },
      { label: 'Supplier' },
      { label: 'Type', width: '90px' },
      { label: 'Value', align: R, mono: true, width: '150px' },
      { label: 'Input tax', align: R, mono: true, width: '130px' },
      { label: 'Outstanding', align: R, mono: true, width: '140px' },
      { label: 'Status', align: R, width: '120px', badge: true },
    ],
    rows: rows.map((p) => [
      p.invoiceNo,
      formatDate(p.invoiceDate),
      p.supplier,
      p.isImport ? 'Import' : 'Local',
      formatMoney(p.grandTotalBase, { decimals: 0 }),
      formatMoney(p.taxTotal, { decimals: 0, dashOnZero: true }),
      formatMoney(p.outstandingBase, { decimals: 0, dashOnZero: true }),
      humanizeStatus(p.status),
    ]),
    total: [
      '',
      '',
      `${rows.length} document${rows.length === 1 ? '' : 's'}`,
      '',
      formatMoney(sumDecimal(rows.map((p) => p.grandTotalBase)), { decimals: 0 }),
      formatMoney(sumDecimal(rows.map((p) => p.taxTotal)), { decimals: 0 }),
      formatMoney(sumDecimal(rows.map((p) => p.outstandingBase)), { decimals: 0 }),
      '',
    ],
  };

  const netOf = (l: PurchaseLineDraft) => {
    const gross = Number(l.enteredQty || 0) * Number(l.unitRate || 0);
    const net = gross - (gross * Number(l.discountPct || 0)) / 100;
    return { net, tax: (net * Number(l.taxPct || 0)) / 100 };
  };
  const totals = lines.reduce(
    (acc, l) => {
      const { net, tax } = netOf(l);
      return { net: acc.net + net, tax: acc.tax + tax };
    },
    { net: 0, tax: 0 },
  );

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chip}
      onChipChange={setChip}
      selectedRow={rows.findIndex((r) => r.invoiceNo === expanded)}
      onRowSelect={(i) =>
        setExpanded(expanded === rows[i]?.invoiceNo ? null : (rows[i]?.invoiceNo ?? null))
      }
      emptyTitle="No purchase in this view"
      hideGrid={open}
      actions={
        <>
          {}
          {!open && (
            <Input
              size="sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Voucher or supplier"
              aria-label="Search purchases"
              className="w-[200px]"
            />
          )}
          <PanelToggle open={open} onToggle={() => setOpen(!open)} label="New local purchase" />
        </>
      }
    >
      <Messages notice={action.notice} problem={action.problem} />

      {}
      {!open && (
        <RegisterWindow range={range} onChange={setRange} count={rows.length} noun="purchase" />
      )}

      {expanded && !open && (
        <DocumentPanel
          doc={purchaseDoc}
          loading={detail.loading}
          error={detail.error}
          onClose={() => setExpanded(null)}
          actions={
            purchaseDoc && (
              <Button size="sm" variant="primary" onClick={() => printDocument(purchaseDoc)}>
                Print invoice
              </Button>
            )
          }
        />
      )}

      <EntryPanel
        title="Local purchase"
        subtitle="Posts the payable, the stock and the input tax in one entry"
        open={open}
        onToggle={() => setOpen(false)}
        openLabel="New local purchase"
        actions={
          <>
            <SubmitHint />
            <Button size="sm" variant="primary" disabled={!postable} onClick={save}>
              Post purchase
            </Button>
          </>
        }
      >
        <FieldRow>
          <TextField
            label="Invoice date"
            type="date"
            required
            value={header.invoiceDate}
            onChange={(v) => setHeader({ ...header, invoiceDate: v })}
          />
          <ComboField
            label="Supplier"
            required
            value={header.supplierCode}
            options={suppliersOfOrigin(
              refs.data?.suppliers ?? [],
              'LOCAL',
              header.supplierCode,
            ).map((s) => ({
              value: s.code,
              label: s.name,
              hint: s.origin === 'FOREIGN' ? 'foreign' : (s.payableAccount ?? undefined),
              keywords: `${s.code} ${s.ntn ?? ''}`,
            }))}
            onChange={(v) => setHeader({ ...header, supplierCode: v })}
          />
          <ComboField
            label="Receiving warehouse"
            required
            value={header.warehouse}
            options={(refs.data?.warehouses ?? []).map((w) => ({
              value: w.code,
              label: w.name,
              hint: w.code,
            }))}
            onChange={(v) => setHeader({ ...header, warehouse: v })}
          />
          <TextField
            label="Supplier invoice no."
            hint="One per supplier — the cheapest duplicate-bill control"
            value={header.supplierInvoiceNo}
            onChange={(v) => setHeader({ ...header, supplierInvoiceNo: v })}
          />
        </FieldRow>

        <EntryGrid
          aria-label="Purchase lines"
          columns={[
            { key: 'no', label: '#', width: '44px', noPrint: true },
            { key: 'sku', label: 'Item', width: '230px', required: true },
            { key: 'batch', label: 'Batch', width: '150px' },
            { key: 'mfg', label: 'Made', width: '130px' },
            { key: 'exp', label: 'Expires', width: '130px' },
            { key: 'qty', label: 'Qty', width: '100px', numeric: true, required: true },
            { key: 'uom', label: 'Unit', width: '84px' },
            { key: 'rate', label: 'Rate', width: '116px', numeric: true, required: true },
            { key: 'disc', label: 'Disc %', width: '84px', numeric: true },
            { key: 'tax', label: 'Tax %', width: '84px', numeric: true },
            { key: 'amount', label: 'Net', width: '130px', numeric: true },
            { key: 'del', label: '', width: '44px', noPrint: true },
          ]}
          footer={
            <tr>
              <td colSpan={10} className="px-[var(--pad)] py-2 text-right text-[12px]">
                Net of discount, plus input tax
              </td>
              <td className="bb-num px-2.5 py-2 text-right text-[13px] tabular-nums">
                {formatMoney(totals.net.toFixed(4), { decimals: 0 })} +{' '}
                {formatMoney(totals.tax.toFixed(4), { decimals: 0 })}
              </td>
              <td />
            </tr>
          }
        >
          {lines.map((line, i) => {
            const update = (patch: Partial<PurchaseLineDraft>) => {
              const next = [...lines];
              next[i] = { ...line, ...patch };
              setLines(next);
            };
            const product = (products.data?.products ?? []).find((p) => p.sku === line.sku);
            const deriveExpiry = (next: {
              sku?: string;
              manufactureDate?: string;
            }): string | undefined => {
              if (line.expiryTouched) return undefined;
              const sku = next.sku ?? line.sku;
              const from = next.manufactureDate ?? line.manufactureDate ?? '';
              const p = (products.data?.products ?? []).find((x) => x.sku === sku);
              if (!p?.trackExpiry) return '';
              return addDays(from || header.invoiceDate, p.shelfLifeDays);
            };
            return (
              <EntryRow key={i}>
                <EntryCell padded className="text-[var(--ink-3)]">
                  {i + 1}
                </EntryCell>
                <EntryCell>
                  <Combobox
                    value={line.sku}
                    aria-label={`Line ${i + 1} item`}
                    placeholder="Type a SKU or item name…"
                    options={(products.data?.products ?? [])
                      .filter((p) => p.isActive && p.isPurchasable)
                      .map((p) => ({
                        value: p.sku,
                        label: `${p.sku} · ${p.name}`,
                        hint: p.trackExpiry && p.shelfLifeDays ? `${p.shelfLifeDays}d` : undefined,
                        keywords: `${p.brand ?? ''} ${p.categoryName ?? ''}`,
                      }))}
                    onChange={(sku) => {
                      const picked = (products.data?.products ?? []).find((p) => p.sku === sku);
                      const expiry = deriveExpiry({ sku });
                      const defaults = pickedLine(
                        sku,
                        products.data?.products,
                        'PURCHASE',
                        line.enteredQty,
                      );
                      update({
                        sku,
                        enteredQty: defaults.qty,
                        uomCode: defaults.uom,
                        taxPct: picked?.purchaseTaxPct ?? line.taxPct,
                        ...(expiry === undefined ? {} : { expiryDate: expiry }),
                      });
                    }}
                  />
                </EntryCell>
                <EntryCell>
                  <GridInput
                    value={line.batchNo}
                    placeholder="auto"
                    aria-label={`Line ${i + 1} batch`}
                    onChange={(e) => update({ batchNo: e.target.value })}
                  />
                </EntryCell>
                <EntryCell>
                  <GridInput
                    type="date"
                    value={line.manufactureDate}
                    aria-label={`Line ${i + 1} manufacture date`}
                    title="Printed on the sack. Leave blank if the supplier does not state one."
                    onChange={(e) => {
                      const expiry = deriveExpiry({ manufactureDate: e.target.value });
                      update({
                        manufactureDate: e.target.value,
                        ...(expiry === undefined ? {} : { expiryDate: expiry }),
                      });
                    }}
                  />
                </EntryCell>
                <EntryCell>
                  <GridInput
                    type="date"
                    value={line.expiryDate}
                    disabled={line.sku !== '' && product?.trackExpiry === false}
                    aria-label={`Line ${i + 1} expiry date`}
                    title={
                      product?.trackExpiry === false
                        ? `${line.sku} is not expiry-tracked`
                        : line.expiryTouched
                          ? 'Typed — no longer follows the shelf life'
                          : product?.shelfLifeDays
                            ? `Suggested: ${product.shelfLifeDays} days of shelf life. Overwrite it with what the sack says.`
                            : 'This product has no default shelf life — set one on Product Management, or type the date.'
                    }
                    onChange={(e) => update({ expiryDate: e.target.value, expiryTouched: true })}
                  />
                </EntryCell>
                <EntryCell>
                  <GridInput
                    numeric
                    value={line.enteredQty}
                    aria-label={`Line ${i + 1} quantity`}
                    onChange={(e) => update({ enteredQty: e.target.value })}
                  />
                </EntryCell>
                <EntryCell>
                  <GridSelect
                    value={line.uomCode}
                    aria-label={`Line ${i + 1} unit`}
                    onChange={(e) => update({ uomCode: e.target.value })}
                  >
                    {}
                    {uomOptions(line.uomCode, product?.baseUom).map((u) => (
                      <option key={u.code} value={u.code}>
                        {u.code}
                      </option>
                    ))}
                  </GridSelect>
                </EntryCell>
                <EntryCell>
                  <GridInput
                    numeric
                    value={line.unitRate}
                    aria-label={`Line ${i + 1} rate`}
                    onChange={(e) => update({ unitRate: e.target.value })}
                  />
                </EntryCell>
                <EntryCell>
                  <GridInput
                    numeric
                    value={line.discountPct}
                    aria-label={`Line ${i + 1} discount`}
                    onChange={(e) => update({ discountPct: e.target.value })}
                  />
                </EntryCell>
                <EntryCell>
                  <GridInput
                    numeric
                    value={line.taxPct}
                    aria-label={`Line ${i + 1} tax`}
                    onChange={(e) => update({ taxPct: e.target.value })}
                  />
                </EntryCell>
                <EntryCell padded numeric>
                  {formatMoney(netOf(line).net.toFixed(4), { decimals: 0, dashOnZero: true })}
                </EntryCell>
                <EntryCell noPrint>
                  <GridAction
                    label={`Remove line ${i + 1}`}
                    disabled={lines.length === 1}
                    onClick={() => setLines(lines.filter((_, j) => j !== i))}
                  >
                    ×
                  </GridAction>
                </EntryCell>
              </EntryRow>
            );
          })}
        </EntryGrid>

        {}
        <div className="flex flex-col">
          <label
            htmlFor="purchase-narration"
            className="bb-eyebrow pb-1.5 text-[10px] text-[var(--ink-3)]"
          >
            Narration
          </label>
          <Textarea
            id="purchase-narration"
            rows={2}
            value={header.narration}
            placeholder="Supplier bill reference, delivery note, terms"
            aria-label="Narration"
            onChange={(e) => setHeader({ ...header, narration: e.target.value })}
          />
        </div>
      </EntryPanel>
    </RecordGrid>
  );
}


interface PurchaseReturnRow {
  returnNo: string;
  returnDate: string;
  supplier: string;
  supplierCode: string;
  againstInvoice: string | null;
  againstGrn: string | null;
  warehouseCode: string;
  warehouseName: string;
  reasonCode: string | null;
  reason: string | null;
  settlementMode: string;
  status: string;
  subtotal: string;
  taxTotal: string;
  grandTotal: string;
  grandTotalBase: string;
  voucherNo: string | null;
  narration: string | null;
  qtyBase: string;
  lineCount: number;
}

interface PurchaseInvoiceLine {
  lineNo: number;
  sku: string;
  productName: string;
  batchNo: string | null;
  qtyBase: string;
  enteredQty: string;
  uomCode: string | null;
  unitRate: string;
  taxPct: string;
  lineTotal: string;
}

export function PurchaseReturnScreen() {
  const [range, setRange] = useState(() => monthOf(companyToday()));
  const res = useResource<{ returns: PurchaseReturnRow[] }>('/purchase-returns', {
    from: range.from || undefined,
    to: range.to || undefined,
  });
  const purchases = useResource<{ purchases: PurchaseRow[] }>('/purchases');
  const refs = useResource<PurchaseRefs>('/purchase-refs');
  const all = useMemo(() => res.data?.returns ?? [], [res.data]);
  const [chip, setChip] = useState(0);
  const [open, setOpen] = useState(false);
  const action = useAction(res.reload);

  const [against, setAgainst] = useState('');
  const [returnDate, setReturnDate] = useState(today());
  const [reasonCode, setReasonCode] = useState('');
  const [settlementMode, setSettlementMode] = useState('CREDIT_NOTE');
  const [quantities, setQuantities] = useState<Record<number, string>>({});

  const source = useResource<{ header: Record<string, string>; lines: PurchaseInvoiceLine[] }>(
    against ? '/purchase' : null,
    { no: against || undefined },
  );

  const [expanded, setExpanded] = useState<string | null>(null);
  const detail = useResource<{ header: Record<string, string>; lines: Record<string, string>[] }>(
    expanded ? '/purchase-return' : null,
    { no: expanded ?? undefined },
  );

  const returnDoc = useMemo<PrintDocument | null>(() => {
    const d = detail.data;
    if (!d || !expanded) return null;
    const h = d.header;
    const currency = h.currency ?? baseCurrency();
    const money = (v: string | null | undefined) => formatMoney(v ?? '0', { currency });
    return {
      title: 'Debit Note — Return to Supplier',
      documentNo: h.returnNo ?? expanded,
      facts: [
        { label: 'Date', value: formatDate(h.returnDate ?? '') },
        {
          label: 'Supplier',
          value: `${h.supplier ?? ''}${h.supplierCode ? ` (${h.supplierCode})` : ''}`,
        },
        { label: 'NTN', value: h.ntn ?? '' },
        { label: 'Against', value: h.againstInvoice ?? h.againstGrn ?? '' },
        { label: 'From warehouse', value: h.warehouseName ?? h.warehouseCode ?? '' },
        { label: 'Reason', value: h.reason ?? '' },
        { label: 'Settlement', value: humanizeStatus(h.settlementMode ?? '') },
        {
          label: 'Currency',
          value: currency === baseCurrency() ? currency : `${currency} @ ${h.fxRate ?? '1'}`,
        },
      ],
      columns: [
        { label: 'Item' },
        { label: 'Batch', width: '150px' },
        { label: 'Qty', align: 'right', width: '110px' },
        { label: 'Rate', align: 'right', width: '110px' },
        { label: 'Tax', align: 'right', width: '90px' },
        { label: 'Amount', align: 'right', width: '130px' },
      ],
      rows: d.lines.map((l) => [
        `${l.sku ?? ''} · ${l.productName ?? ''}`,
        l.batchNo ?? '—',
        `${formatQty(l.enteredQty, 0)} ${l.uomCode ?? ''}`,
        formatRate(l.unitRate, 2),
        `${Number(l.taxPct ?? 0)}%`,
        money(l.lineTotal),
      ]),
      totals: [
        { label: 'Subtotal', value: money(h.subtotal) },
        ...(Number(h.taxTotal ?? 0) !== 0
          ? [{ label: 'Input tax', value: money(h.taxTotal) }]
          : []),
        { label: 'Debited', value: money(h.grandTotal), strong: true },
        ...(currency !== baseCurrency()
          ? [{ label: `Booked in ${baseCurrency()}`, value: formatMoney(h.grandTotalBase ?? '0') }]
          : []),
      ],
      narration: h.narration ?? null,
      notes: [
        h.voucherNo ? `Posted as ${h.voucherNo}.` : 'Not posted to the general ledger.',
        'The supplier is debited at the original purchase price and the stock is released at the cost that purchase put into the warehouse — not at today’s average.',
      ],
      signatures: ['Despatched by', 'Supplier acknowledgement'],
    };
  }, [detail.data, expanded]);

  useEffect(() => setQuantities({}), [against]);

  const reasons = (refs.data?.reasonCodes ?? []).filter((r) => r.domain === 'PURCHASE_RETURN');

  useEffect(() => {
    if (reasonCode === '' && reasons.length === 1 && reasons[0]) setReasonCode(reasons[0].code);
  }, [reasons, reasonCode]);
  const filters: Array<{ label: string; test: (r: PurchaseReturnRow) => boolean }> = [
    { label: 'All reasons', test: () => true },
    ...reasons.slice(0, 3).map((r) => ({
      label: r.name,
      test: (row: PurchaseReturnRow) => row.reasonCode === r.code,
    })),
  ];
  const rows = useMemo(
    () => all.filter(filters[chip]?.test ?? (() => true)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, chip],
  );

  const save = () => {
    const lines = Object.entries(quantities)
      .filter(([, qty]) => qty.trim() !== '' && Number(qty) > 0)
      .map(([lineNo, qty]) => ({ lineNo: Number(lineNo), enteredQty: qty.trim() }));
    if (lines.length === 0) return;
    void action.run(() =>
      apiSend<{ returnNo: string; message: string }>(
        'POST',
        '/purchase-returns',
        {
          returnDate,
          purchaseInvoiceNo: against,
          reasonCode: reasonCode || undefined,
          settlementMode,
          lines,
        },
        auth(),
      ).then((r) => {
        setOpen(false);
        setAgainst('');
        setQuantities({});
        return r;
      }),
    );
  };

  const postable =
    !action.busy &&
    against !== '' &&
    Object.values(quantities).some((q) => q.trim() !== '' && Number(q) > 0);
  useSubmitShortcut(open && postable, save);

  const set: RecordSet = {
    crumb: 'Sales & Purchase / Purchase Return',
    title: 'Purchase return / debit note',
    subtitle: 'Goods sent back to suppliers, against the purchase they came in on.',
    chips: filters.map((f) => f.label),
    cols: [
      { label: 'Debit note', width: '150px', mono: true },
      { label: 'Against', width: '150px', mono: true },
      { label: 'Supplier' },
      { label: 'Reason' },
      { label: 'Qty (Kg)', align: R, mono: true, width: '120px' },
      { label: 'Value', align: R, mono: true, width: '150px' },
      { label: 'Settlement', align: R, width: '150px', badge: true },
    ],
    rows: rows.map((r) => [
      r.returnNo,
      r.againstInvoice ?? r.againstGrn ?? '—',
      r.supplier,
      r.reason ?? '—',
      formatQty(r.qtyBase, 0),
      formatMoney(r.grandTotalBase, { decimals: 0 }),
      humanizeStatus(r.settlementMode),
    ]),
    total: [
      '',
      '',
      '',
      `${rows.length} note${rows.length === 1 ? '' : 's'}`,
      formatQty(sumDecimal(rows.map((r) => r.qtyBase)), 0),
      formatMoney(sumDecimal(rows.map((r) => r.grandTotalBase)), { decimals: 0 }),
      '',
    ],
  };

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chip}
      onChipChange={setChip}
      selectedRow={rows.findIndex((r) => r.returnNo === expanded)}
      onRowSelect={(i) =>
        setExpanded(expanded === rows[i]?.returnNo ? null : (rows[i]?.returnNo ?? null))
      }
      emptyTitle="No debit note raised yet"
      hideGrid={open}
      actions={<PanelToggle open={open} onToggle={() => setOpen(!open)} label="New debit note" />}
    >
      <Messages notice={action.notice} problem={action.problem} />

      {}
      {!open && (
        <RegisterWindow range={range} onChange={setRange} count={rows.length} noun="return" />
      )}

      {expanded && !open && (
        <DocumentPanel
          doc={returnDoc}
          loading={detail.loading}
          error={detail.error}
          onClose={() => setExpanded(null)}
          actions={
            returnDoc && (
              <Button size="sm" variant="primary" onClick={() => printDocument(returnDoc)}>
                Print debit note
              </Button>
            )
          }
        />
      )}

      <EntryPanel
        title="Return to supplier"
        subtitle="Pick the purchase it came in on — the return is valued and capped from that document"
        open={open}
        onToggle={() => setOpen(false)}
        openLabel="New debit note"
        actions={
          <>
            <SubmitHint />
            <Button size="sm" variant="primary" disabled={!postable} onClick={save}>
              Post debit note
            </Button>
          </>
        }
      >
        <FieldRow>
          <ComboField
            label="Against purchase"
            required
            caption="The document the goods came in on — it decides both the credit and the cost"
            value={against}
            options={(purchases.data?.purchases ?? [])
              .filter((p) => p.status === 'POSTED')
              .map((p) => ({
                value: p.invoiceNo,
                label: `${p.invoiceNo} · ${p.supplier}`,
                hint: formatMoney(p.grandTotalBase, { decimals: 0 }),
                keywords: p.supplierInvoiceNo ?? '',
              }))}
            onChange={setAgainst}
          />
          <TextField
            label="Return date"
            type="date"
            required
            value={returnDate}
            onChange={setReturnDate}
          />
          <SelectField
            label="Reason"
            value={reasonCode}
            options={reasons.map((r) => ({ value: r.code, label: r.name }))}
            onChange={setReasonCode}
          />
          <SelectField
            label="Settlement"
            value={settlementMode}
            placeholder=""
            options={[
              { value: 'CREDIT_NOTE', label: 'Supplier credit note' },
              { value: 'REPLACEMENT', label: 'Replacement shipment' },
              { value: 'CASH_REFUND', label: 'Cash refund' },
              { value: 'WRITE_OFF', label: 'Written off' },
            ]}
            onChange={setSettlementMode}
          />
        </FieldRow>

        {against && (
          <EntryGrid
            aria-label="Returnable lines"
            columns={[
              { key: 'no', label: 'Line', width: '70px' },
              { key: 'sku', label: 'Item' },
              { key: 'batch', label: 'Batch', width: '170px' },
              { key: 'bought', label: 'Bought', width: '130px', numeric: true },
              { key: 'rate', label: 'Rate', width: '120px', numeric: true },
              { key: 'ret', label: 'Return qty', width: '140px', numeric: true },
            ]}
          >
            {(source.data?.lines ?? []).map((l) => (
              <EntryRow key={l.lineNo}>
                <EntryCell padded className="text-[var(--ink-3)]">
                  {l.lineNo}
                </EntryCell>
                <EntryCell padded>
                  <span className="bb-num pr-2 text-[12px] text-[var(--accent)]">{l.sku}</span>
                  {l.productName}
                </EntryCell>
                <EntryCell padded className="bb-num text-[var(--ink-2)]">
                  {l.batchNo ?? '—'}
                </EntryCell>
                <EntryCell padded numeric>
                  {formatQty(l.enteredQty, 0)} {l.uomCode}
                </EntryCell>
                <EntryCell padded numeric>
                  {formatRate(l.unitRate, 2)}
                </EntryCell>
                <EntryCell>
                  <GridInput
                    numeric
                    value={quantities[l.lineNo] ?? ''}
                    placeholder={`≤ ${formatQty(l.enteredQty, 0)}`}
                    aria-label={`Return quantity for line ${l.lineNo}`}
                    onChange={(e) => setQuantities({ ...quantities, [l.lineNo]: e.target.value })}
                  />
                </EntryCell>
              </EntryRow>
            ))}
          </EntryGrid>
        )}
      </EntryPanel>

      {}
      {!open && (
        <Tiles>
          <Card>
            <StatTile label="Debit notes" value={String(rows.length)} hint="in this view" />
          </Card>
          <Card>
            <StatTile
              label="Value returned"
              value={formatMoney(sumDecimal(rows.map((r) => r.grandTotalBase)), { decimals: 0 })}
              hint="at the original price"
            />
          </Card>
          <Card>
            <StatTile
              label="Quantity returned"
              value={`${formatQty(sumDecimal(rows.map((r) => r.qtyBase)), 0)} Kg`}
              hint="out of the warehouse"
            />
          </Card>
          <Card>
            <StatTile
              label="Awaiting credit"
              value={String(rows.filter((r) => r.settlementMode === 'CREDIT_NOTE').length)}
              hint="supplier has not credited yet"
            />
          </Card>
        </Tiles>
      )}
    </RecordGrid>
  );
}
